import DocumentViewerModal from "components/DocumentViewer";
import { StatusChip } from "components/StatusChip";
import { useTranslation } from "contexts/i18nContext";
import { useHasRole } from "contexts/AuthContext";
import { useState } from "react";
import { COMPLIANT_STATUS, DECISION_TYPE } from "utils/constantValues";
import { ROLES } from "utils/roles";
import AddComplaintDecisionModal from "./AddComplaintDecisionModal";
import { useAuth } from "contexts/AuthContext";

export default function ComplaintDetailInfoCard({
  complaintData,
  onComplaintsRefresh,
}: any) {
  const canCreateComplaintDecision = useHasRole([
    ROLES.INSPECTION_COORDINATOR,
    ROLES.INSPECTION_DIRECTOR,
  ]);
  const { user } = useAuth();

  const [isOpen, setIsOpen] = useState(false);
  const [expandedDecisions, setExpandedDecisions] = useState<number[]>([]);

  const { t } = useTranslation();

  // Determine if complaint is closed/terminated for special styling
  const isClosedOrTerminated =
    complaintData?.status === COMPLIANT_STATUS.CLOSED ||
    complaintData?.status === COMPLIANT_STATUS.TERMINATED;

  // Get latest decision (most recent)
  const latestDecision =
    complaintData?.decision_history?.length > 0
      ? complaintData.decision_history[
          complaintData.decision_history.length - 1
        ]
      : null;

  // Get previous decisions (all except the latest)
  const previousDecisions =
    complaintData?.decision_history?.length > 1
      ? complaintData.decision_history.slice(0, -1).reverse() // Reverse to show most recent first
      : [];

  const toggleDecision = (index: number) => {
    setExpandedDecisions((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index],
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Main Complaint Card with Gray Background */}
      <div
        className={`mx-auto rounded-2xl overflow-hidden mb-8 shadow-lg transition-all duration-300 hover:shadow-xl ${
          isClosedOrTerminated
            ? "border-l-8 border-red-600 bg-gradient-to-r from-white to-gray-50"
            : "border-l-8 border-gray-600 bg-gradient-to-r from-white to-gray-50"
        }`}
      >
        {/* Subtle Gray Header */}
        <div className="bg-gradient-to-r from-gray-700 via-gray-600 to-gray-800 h-2"></div>

        <div className="p-6">
          {/* Accused Prosecutor Section */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6 p-4 bg-white rounded-xl border border-gray-200 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-50 rounded-lg">
                <svg
                  className="w-6 h-6 text-red-800"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                  />
                </svg>
              </div>
              <h1 className="text-xl font-bold text-gray-900">
                {t("accusedProsecutor")}
              </h1>
            </div>
            <div className="text-right">
              <p className="text-lg font-semibold text-gray-800">
                {complaintData?.accused_prosecutor_name || "-"}
              </p>
              {complaintData?.accused_prosecutor_gender && (
                <p className="text-sm text-gray-600 mt-1">
                  {complaintData.accused_prosecutor_gender}
                </p>
              )}
            </div>
          </div>

          {/* Status Section */}
          <div className="mb-8 p-5 bg-white rounded-xl border border-gray-200 shadow-sm">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-50 rounded-lg">
                  <svg
                    className="w-6 h-6 text-red-800"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">
                    Case Status
                  </h2>
                  <p className="text-sm text-gray-600">
                    Current complaint status
                  </p>
                </div>
              </div>
              <StatusChip status={complaintData?.status || ""} />
            </div>

            {/* Grid Layout for Complaint Details */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Column 1 */}
              <div className="space-y-4">
                <div className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-2 mb-1">
                    <p className="text-sm font-medium text-gray-500">
                      {t("offenseType")}
                    </p>
                  </div>
                  <p className="text-base font-semibold text-gray-900">
                    {complaintData?.offence_type || "-"}
                  </p>
                </div>

                <div className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-2 mb-1">
                    <p className="text-sm font-medium text-gray-500">
                      {t("receptionDate")}
                    </p>
                  </div>
                  <p className="text-base font-semibold text-gray-900">
                    {complaintData?.reception_date || "-"}
                  </p>
                </div>

                <div className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-2 mb-1">
                    <p className="text-sm font-medium text-gray-500">
                      Job Status
                    </p>
                  </div>
                  <p className="text-base font-semibold text-gray-900">
                    {complaintData?.accused_prosecutor_job_status || "-"}
                  </p>
                </div>
              </div>

              {/* Column 2 */}
              <div className="space-y-4">
                <div className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-2 mb-1">
                    <p className="text-sm font-medium text-gray-500">
                      Prosecutor Offence Region
                    </p>
                  </div>
                  <p className="text-base font-semibold text-gray-900">
                    {complaintData?.prosecutor_offence_region || "-"}
                  </p>
                </div>

                <div className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-2 mb-1">
                    <p className="text-sm font-medium text-gray-500">
                      Prosecutor Offence Zone
                    </p>
                  </div>
                  <p className="text-base font-semibold text-gray-900">
                    {complaintData?.prosecutor_offence_zone || "-"}
                  </p>
                </div>

                <div className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-2 mb-1">
                    <p className="text-sm font-medium text-gray-500">
                      Prosecutor Offence Woreda
                    </p>
                  </div>
                  <p className="text-base font-semibold text-gray-900">
                    {complaintData?.prosecutor_offence_woreda || "-"}
                  </p>
                </div>
              </div>

              {/* Column 3 - Transferred From Section */}
              {complaintData?.transferred_from_admin_unit_id && (
                <div className="p-4 bg-gray-100 border border-gray-300 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <svg
                      className="w-5 h-5 text-red-800"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"
                      />
                    </svg>
                    <p className="text-sm font-medium text-gray-700">
                      Transferred Case
                    </p>
                  </div>
                  <p className="text-base font-semibold text-gray-900">
                    {complaintData?.transferred_from_admin_unit_id}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    Transferred from another unit
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Offense Description */}
          {complaintData?.offence_description && (
            <div className="mb-6 p-5 bg-white rounded-xl border border-gray-200 shadow-sm">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-red-50 rounded-lg">
                  <svg
                    className="w-6 h-6 text-red-800"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.698-.833-2.464 0L4.232 16.5c-.77.833.192 2.5 1.732 2.5z"
                    />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Offense Description
                </h3>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                  {complaintData.offence_description}
                </p>
              </div>
            </div>
          )}

          {/* Termination Information (if applicable) */}
          {complaintData?.termination_reason && (
            <div className="mb-6 p-5 bg-gray-100 border border-gray-300 rounded-xl">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-red-100 rounded-lg">
                  <svg
                    className="w-6 h-6 text-red-800"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {t("terminationReason")}
                </h3>
              </div>
              <p className="text-gray-800 leading-relaxed p-3 bg-white rounded-lg border border-gray-200">
                {complaintData.termination_reason}
              </p>
            </div>
          )}

          {/* Order Given By (if applicable) */}
          {complaintData?.order_given_by && (
            <div className="mb-6 p-5 bg-gray-100 border border-gray-300 rounded-xl">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-red-50 rounded-lg">
                  <svg
                    className="w-6 h-6 text-red-800"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {t("orderGivenBy")}
                </h3>
              </div>
              <p className="text-gray-800 leading-relaxed p-3 bg-white rounded-lg border border-gray-200">
                {complaintData.order_given_by}
              </p>
            </div>
          )}

          {/* Termination Documents */}
          {complaintData?.termination_documents &&
            complaintData?.termination_documents.length > 0 && (
              <div className="mt-6">
                <DocumentViewerModal
                  documents={complaintData?.termination_documents}
                  baseUrl={`${process.env.REACT_APP_API_BASE_URL}prosecutor-decipiline/complaint-termination-documents/document`}
                />
              </div>
            )}
        </div>
      </div>

      {/* Document Information Card */}
      <div className="mx-auto bg-white rounded-2xl overflow-hidden mb-8 shadow-lg border border-gray-200">
        <div className="p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-red-50 rounded-lg">
              <svg
                className="w-6 h-6 text-red-800"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
            </div>
            <h1 className="text-xl font-bold text-gray-900">
              {t("documentInformation")}
            </h1>
          </div>

          {/* Receiving Prosecutor Card */}
          <div className="mb-6 p-5 bg-gray-50 rounded-xl border border-gray-300">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="relative">
                  <img
                    src={require("../../../images/avatar.png")}
                    alt="avatar"
                    className="w-12 h-12 rounded-full border-2 border-white shadow-sm"
                  />
                  <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-red-800 rounded-full border-2 border-white"></div>
                </div>
                <div>
                  <p className="font-semibold text-gray-900 text-base">
                    {complaintData?.receiving_prosecutor || "-"}
                  </p>
                  <div className="flex items-center gap-2">
                    <span className="text-xs px-2 py-1 bg-gray-200 text-gray-700 rounded-full">
                      {t("receivedBy")}
                    </span>
                    <span className="text-xs text-gray-500">• Active</span>
                  </div>
                </div>
              </div>
              <button className="text-red-800 hover:text-red-900 p-2 rounded-full hover:bg-gray-100">
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </button>
            </div>
          </div>

          {/* NEW: Mobile Report Information */}
          {complaintData?.report_application_number && (
            <div className="mb-6 p-4 bg-gray-50 rounded-xl border border-gray-300">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-red-50 rounded-lg">
                  <svg
                    className="w-5 h-5 text-red-800"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"
                    />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Accusation Report
                </h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-3 bg-white rounded-lg border border-gray-200">
                  <div className="flex items-center gap-2 mb-1">
                    <svg
                      className="w-4 h-4 text-red-800"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"
                      />
                    </svg>
                    <p className="text-xs font-medium text-gray-600">
                      Submitted via Mobile
                    </p>
                  </div>
                  <p className="text-sm font-semibold text-gray-900">Yes</p>
                </div>
                {complaintData?.report_application_number && (
                  <div className="p-3 bg-white rounded-lg border border-gray-200">
                    <div className="flex items-center gap-2 mb-1">
                      <svg
                        className="w-4 h-4 text-red-800"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14"
                        />
                      </svg>
                      <p className="text-xs font-medium text-gray-600">
                        Application Number
                      </p>
                    </div>
                    <p className="text-sm font-semibold text-gray-900">
                      {complaintData.report_application_number}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Latest Decision */}
          {latestDecision && (
            <div className="mb-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  Latest Decision
                </h3>
                <span className="px-3 py-1 bg-red-50 text-red-800 text-sm font-medium rounded-full">
                  Current
                </span>
              </div>

              <div className="p-5 bg-gray-50 rounded-xl border border-gray-300">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  {latestDecision?.date && (
                    <div className="p-3 bg-white rounded-lg border border-gray-200">
                      <div className="flex items-center gap-2 mb-1">
                        <svg
                          className="w-4 h-4 text-red-800"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                          />
                        </svg>
                        <p className="text-xs font-medium text-gray-600">
                          {t("registeredBy")}
                        </p>
                      </div>
                      <p className="text-sm font-semibold text-gray-900">
                        {"-"}
                      </p>
                    </div>
                  )}

                  {latestDecision?.assigned_prosecutor_detail?.full_name && (
                    <div className="p-3 bg-white rounded-lg border border-gray-200">
                      <div className="flex items-center gap-2 mb-1">
                        <svg
                          className="w-4 h-4 text-red-800"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                          />
                        </svg>
                        <p className="text-xs font-medium text-gray-600">
                          {t("assignedTo")}
                        </p>
                      </div>
                      <p className="text-sm font-semibold text-gray-900">
                        {latestDecision?.assigned_prosecutor_detail
                          ?.full_name || ""}
                      </p>
                    </div>
                  )}
                </div>

                {/* Decision Type Badge */}
                {latestDecision?.decision_type && (
                  <div className="flex items-center gap-2 mb-4">
                    <div className="p-2 bg-red-100 rounded-lg">
                      <svg
                        className="w-5 h-5 text-red-800"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                        />
                      </svg>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-600">
                        Decision Type
                      </p>
                      <p className="text-lg font-bold text-gray-900">
                        {latestDecision.decision_type}
                      </p>
                    </div>
                  </div>
                )}

                {/* Decision Summary */}
                {latestDecision?.decision_summary && (
                  <div className="p-4 bg-white rounded-lg border border-gray-200">
                    <div className="flex items-center gap-2 mb-3">
                      <svg
                        className="w-4 h-4 text-red-800"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z"
                        />
                      </svg>
                      <h3 className="text-sm font-semibold text-gray-900">
                        {t("decisionSummary")}
                      </h3>
                    </div>
                    <p className="text-gray-700 leading-relaxed text-sm">
                      {latestDecision.decision_summary}
                    </p>
                  </div>
                )}

                {/* Out of Jurisdiction Source */}
                {latestDecision?.out_of_jursdicition_source && (
                  <div className="mt-4 p-3 bg-gray-100 border border-gray-300 rounded-lg">
                    <div className="flex items-center gap-2 mb-1">
                      <svg
                        className="w-4 h-4 text-red-800"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      <p className="text-sm font-medium text-gray-700">
                        Out of Jurisdiction Source
                      </p>
                    </div>
                    <p className="text-sm font-semibold text-gray-900">
                      {latestDecision.out_of_jursdicition_source}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Previous Decisions Accordion */}
          {previousDecisions.length > 0 && (
            <div className="mt-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Previous Decisions ({previousDecisions.length})
              </h3>

              <div className="space-y-3">
                {previousDecisions.map((decision: any, index: number) => {
                  const isExpanded = expandedDecisions.includes(index);
                  return (
                    <div
                      key={index}
                      className="border border-gray-300 rounded-xl overflow-hidden"
                    >
                      {/* Accordion Header */}
                      <button
                        onClick={() => toggleDecision(index)}
                        className="w-full p-4 bg-gray-100 hover:bg-gray-200 transition-colors flex items-center justify-between"
                      >
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-white rounded-lg border border-gray-300">
                            <svg
                              className="w-4 h-4 text-red-800"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                              />
                            </svg>
                          </div>
                          <div className="text-left">
                            <p className="font-medium text-gray-900">
                              {decision.decision_type || "Decision"}
                            </p>
                            {decision.assigned_prosecutor_detail?.full_name && (
                              <p className="text-sm text-gray-600">
                                Assigned to:{" "}
                                {decision.assigned_prosecutor_detail.full_name}
                              </p>
                            )}
                          </div>
                        </div>
                        <svg
                          className={`w-5 h-5 text-red-800 transition-transform ${isExpanded ? "rotate-180" : ""}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </button>

                      {/* Accordion Content */}
                      {isExpanded && (
                        <div className="p-5 bg-white">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                            {decision?.date && (
                              <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                                <div className="flex items-center gap-2 mb-1">
                                  <svg
                                    className="w-4 h-4 text-red-800"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth="2"
                                      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                                    />
                                  </svg>
                                  <p className="text-xs font-medium text-gray-600">
                                    {t("registeredBy")}
                                  </p>
                                </div>
                                <p className="text-sm font-semibold text-gray-900">
                                  {"-"}
                                </p>
                              </div>
                            )}

                            {decision?.assigned_prosecutor_detail
                              ?.full_name && (
                              <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                                <div className="flex items-center gap-2 mb-1">
                                  <svg
                                    className="w-4 h-4 text-red-800"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth="2"
                                      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                                    />
                                  </svg>
                                  <p className="text-xs font-medium text-gray-600">
                                    {t("assignedTo")}
                                  </p>
                                </div>
                                <p className="text-sm font-semibold text-gray-900">
                                  {decision?.assigned_prosecutor_detail
                                    ?.full_name || ""}
                                </p>
                              </div>
                            )}
                          </div>

                          {/* Decision Summary */}
                          {decision?.decision_summary && (
                            <div className="mb-4">
                              <div className="flex items-center gap-2 mb-2">
                                <svg
                                  className="w-4 h-4 text-red-800"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z"
                                  />
                                </svg>
                                <h4 className="text-sm font-semibold text-gray-900">
                                  {t("decisionSummary")}
                                </h4>
                              </div>
                              <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                                <p className="text-gray-700 text-sm">
                                  {decision.decision_summary}
                                </p>
                              </div>
                            </div>
                          )}

                          {/* Out of Jurisdiction Source */}
                          {decision?.out_of_jursdicition_source && (
                            <div className="mb-4">
                              <div className="flex items-center gap-2 mb-2">
                                <svg
                                  className="w-4 h-4 text-red-800"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                  />
                                </svg>
                                <h4 className="text-sm font-semibold text-gray-900">
                                  Out of Jurisdiction Source
                                </h4>
                              </div>
                              <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                                <p className="text-gray-700 text-sm">
                                  {decision.out_of_jursdicition_source}
                                </p>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Expand/Collapse All Button */}
              {previousDecisions.length > 1 && (
                <div className="mt-4 text-center">
                  <button
                    onClick={() => {
                      if (
                        expandedDecisions.length === previousDecisions.length
                      ) {
                        setExpandedDecisions([]);
                      } else {
                        setExpandedDecisions(
                          previousDecisions.map((_: any, i: number) => i),
                        );
                      }
                    }}
                    className="text-red-800 hover:text-red-900 font-medium text-sm flex items-center gap-1 mx-auto"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                    {expandedDecisions.length === previousDecisions.length
                      ? "Collapse All"
                      : "Expand All"}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* No Decisions Message */}
          {!complaintData?.decision_history ||
            (complaintData.decision_history.length === 0 && (
              <div className="p-6 bg-gray-50 rounded-xl border border-gray-300 text-center">
                <svg
                  className="w-12 h-12 text-gray-400 mx-auto mb-3"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                <p className="text-gray-600">
                  No decisions have been made yet.
                </p>
              </div>
            ))}
        </div>
      </div>

      {/* Add Decision Button */}
      {canCreateComplaintDecision &&
        user?.admin_unit === complaintData?.admin_unit_id &&
        (complaintData?.decision_type !== DECISION_TYPE?.APPROVED ||
          (complaintData?.decision_type === DECISION_TYPE?.APPROVED &&
            complaintData?.status === COMPLIANT_STATUS.FORWARDED)) && (
          <div className="text-center mb-8">
            <button
              className="bg-gradient-to-r from-red-800 to-red-900 text-white px-8 py-3 rounded-xl font-semibold hover:from-red-900 hover:to-red-950 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1 flex items-center gap-2 mx-auto"
              type="submit"
              onClick={() => setIsOpen(true)}
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                />
              </svg>
              {t("addDecision")}
            </button>
          </div>
        )}

      <AddComplaintDecisionModal
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        complaintData={complaintData}
        onRefresh={onComplaintsRefresh}
      />
    </div>
  );
}
